import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BuyMeACoffeeButton from '../components/BuyMeACoffeeButton';
import '../App.css';
import './Page.css';

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export default function PrivacyPolicy() {
  const [theme, setTheme] = useState('dark');
  const [logoSrc, setLogoSrc] = useState('/logo.png');
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Get initial theme: check localStorage first, then browser preference, default to dark
    const getInitialTheme = () => {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme;
      }
      // Check browser preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
        return 'light';
      }
      return 'dark';
    };
    
    const initialTheme = getInitialTheme();
    setTheme(initialTheme);
    document.documentElement.className = initialTheme === 'light' ? 'light' : '';
    
    // Trigger page load animation
    setIsPageLoaded(true);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.className = newTheme === 'light' ? 'light' : '';
  };

  const handleLogoError = () => {
    if (logoSrc !== '/logo.svg') setLogoSrc('/logo.svg');
  };

  const footerLogo = '/luther.png';
  const toggleTooltip = theme === 'dark' ? 'Light Mode' : 'Dark Mode';
  const refreshPage = () => window.location.href = '/';
  const onBrandKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      refreshPage();
    }
  };

  return (
    <div className="app-container">
      <header className={`app-header ${isPageLoaded ? 'fade-in-blur' : ''}`}>
        <div className="header-inner">
          <div 
            className="brand" 
            onClick={refreshPage}
            onKeyDown={onBrandKeyDown}
            tabIndex={0}
            role="button"
            aria-label="Go to home"
          >
            <div className="brand-logo-wrapper">
              <img src={logoSrc} alt="Colority" className="brand-logo-blur" onError={handleLogoError} />
              <img src={logoSrc} alt="Colority" className="brand-logo" onError={handleLogoError} />
            </div>
            <h1 className="app-title">Colority</h1>
          </div>
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={toggleTooltip}
            data-tooltip={toggleTooltip}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>
        </div>
      </header>
      <div className="page-container">
        <div className="page-content">
        <h1>Privacy Policy</h1>
        
        <section>
          <p>
            <strong>Effective date:</strong> 01.12.2025
          </p>
        </section>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Colority respects your privacy. This privacy policy explains how information is handled when you use the Colority tool. The tool is designed to minimize data processing, and no personal data is transmitted or stored on any server.
          </p>
        </section>

        <section>
          <h2>2. Controller</h2>
          <p>The data controller for this website is:</p>
          <p>
            Niklas Luther<br />
            Dompfaffweg 16<br />
            89269 Vöhringen, Germany<br />
            E-mail: <a href="mailto:hello@luther.studio" target="_blank" rel="noopener noreferrer">hello@luther.studio</a><br />
            VAT-ID: DE327430586
          </p>
        </section>

        <section>
          <h2>3. Data Processing</h2>
          
          <p className="subsection-heading"><strong>3.1 Image Processing</strong></p>
          <p>
            Images uploaded to the Colority tool are processed entirely in your browser. No images or pixel data are sent to any server or third-party service.
          </p>

          <p className="subsection-heading"><strong>3.2 Local Storage</strong></p>
          <p>
            The tool uses localStorage only to remember your theme preference (light or dark mode).
          </p>

          <p className="subsection-heading"><strong>3.3 Cookies and Tracking</strong></p>
          <p>
            No cookies are used. No analytics or tracking services are currently active.
          </p>

          <p className="subsection-heading"><strong>3.4 External Links and Widgets</strong></p>
          <p>
            This website contains links to external websites, specifically the creator's portfolio (luther.studio), the support platform "Buy Me a Coffee", and Product Hunt. The "Buy Me a Coffee" button is implemented as a static hyperlink. The Product Hunt badge is implemented as a static image link that loads from Product Hunt's servers. When you visit Colority, loading the Product Hunt badge image may result in your IP address and browser information being transmitted to Product Hunt's servers. No other personal data is transmitted while you are visiting Colority. If you click these links and leave the Colority website, the privacy policy of the respective destination site applies.
          </p>

          <p className="subsection-heading"><strong>3.5 Future Services</strong></p>
          <p>
            If analytics, tracking, or other data-collecting services are added in the future, they will be clearly disclosed in an updated privacy policy.
          </p>
        </section>

        <section>
          <h2>4. Your Rights</h2>
          <p>
            Even with minimal data usage, users have the following rights under GDPR:
          </p>
          <p>
            Access, correction, or deletion of personal data (Art. 15–17 GDPR)<br />
            Restriction or objection to processing (Art. 18–21 GDPR)<br />
            Lodging complaints with a supervisory authority (Art. 77 GDPR)
          </p>
          <p>
            Since Colority does not store personal data beyond the theme preference, these rights are limited in practice.
          </p>
        </section>

        <section>
          <h2>5. Legal Notice / Imprint</h2>
          <p>
            For information required under German law (Section 5 TMG), please see our <Link to="/imprint">Imprint</Link>.
          </p>
        </section>
        </div>
      </div>
      <footer className={`app-footer ${isPageLoaded ? 'fade-in-blur fade-delay-3' : ''}`}>
        <div className="footer-top-row">
          <div className="footer-left">
            <div className="footer-left-desktop-row">
              <div className="footer-left-top">
                <img className="footer-logo" src={footerLogo} alt="Luther Studio" />
                <span>By <a href="https://www.luther.studio/index.html" className="footer-brand-link" rel="noopener noreferrer" target="_blank">Luther Studio</a> © 2025</span>
              </div>
              <div className="footer-links-group">
                <Link to="/imprint" className={`footer-link ${location.pathname === '/imprint' ? 'active' : ''}`}>Imprint</Link>
                <Link to="/privacy-policy" className={`footer-link ${location.pathname === '/privacy-policy' ? 'active' : ''}`}>Privacy Policy</Link>
              </div>
            </div>
          </div>
          <div className="footer-right">
            <BuyMeACoffeeButton theme={theme} />
            <a 
              href="https://www.producthunt.com/products/colority-net?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-colority" 
              target="_blank" 
              rel="noopener noreferrer"
              className="producthunt-widget"
            >
              <img 
                src={`https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1045069&theme=${theme}&t=1764866734409`}
                alt="Colority - Extract colors from images | Product Hunt" 
                className="producthunt-badge"
              />
            </a>
          </div>
        </div>
        <div className="footer-bottom-row">
          <BuyMeACoffeeButton className="bmc-mobile" theme={theme} />
          <a 
            href="https://www.producthunt.com/products/colority-net?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-colority" 
            target="_blank" 
            rel="noopener noreferrer"
            className="producthunt-widget producthunt-mobile"
          >
            <img 
              src={`https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1045069&theme=${theme}&t=1764866734409`}
              alt="Colority - Extract colors from images | Product Hunt" 
              className="producthunt-badge"
            />
          </a>
        </div>
      </footer>
    </div>
  );
}

